import { graphql } from 'gatsby';
import React from 'react';

import { metaFragment } from 'graphql/metaFragment';
import { personFragment } from 'graphql/personFragment';
import { articleFragment } from 'graphql/articleFragment';
import { pageFragment } from 'graphql/pageFragment';
import { linkableFragment } from 'graphql/linkableFragment';

import { getLang } from 'utils/getLangPath';

import { asText } from 'components/rich-text/RichText';
import { Image } from 'components/image/Image';
import { Slices } from 'containers/slices/SliceContainer';
import { Header } from 'components/header/Header';
import { Prismic_Project, Prismic_ProjectRelated_Projects } from '../types/prismic';
import { ArticleHero } from 'components/article-hero/ArticleHero';
import { projectSlices } from 'graphql/slices';
import { Seo } from 'components/seo/Seo';
import { PrismicImage } from 'types/prismicImage';
import { projectFragment } from 'graphql/projectFragment';
import { Segment } from 'components/segment/Segment';
import { Grid } from 'components/grid/Grid';
import { Card } from 'components/card/Card';
import { ExcludesNull } from 'types/excludesNull';
import { linkResolver } from 'gatsby-source-prismic-graphql';
import { ImageContainer } from 'containers/slices/ImageContainer';
import { useIntl } from 'react-intl';
import { associationFragment } from '../graphql/associationFragment';
import { ArrowButton } from 'components/arrow-button/ArrowButton';

interface IProjectProps {
  data?: {
    prismic?: {
      project?: Prismic_Project;
    };
  };
}

export default function Project(props: IProjectProps) {
  const project: Prismic_Project | null = props?.data?.prismic?.project ?? null;

  if (!project) {
    return null;
  }

  const intl = useIntl();
  const { locale } = intl;

  const relatedProjects = project.related_projects ?? [];

  return (
    <>
      <Seo
        title={asText(project?.seo_title) || asText(project.title)}
        description={asText(project?.seo_description)}
        image={((project?.seo_image ?? null) || project.image) as PrismicImage}
      />

      <Header logo={false}>
        <ArrowButton to={`${getLang(locale)}/verkefni`}>
          {intl.formatMessage({ id: 'projects.back', defaultMessage: 'Aftur í Verkefni' })}
        </ArrowButton>
      </Header>

      <ArticleHero heading={asText(project?.title)}>
        <Image
          url={project?.image?.url}
          width={project?.image?.dimensions?.width}
          height={project?.image?.dimensions?.height}
          alt={project?.image?.alt}
          setRatio={true}
        />
      </ArticleHero>
      <Slices data={project.body ?? []}></Slices>

      {relatedProjects.length > 0 && (
        <Segment isColumnLayout={false}>
          {/* TODO i18n */}
          <Grid
            heading={intl.formatMessage({
              id: 'projects.related',
              defaultMessage: 'Tengd verkefni',
            })}
            link={`${getLang(locale)}/verkefni`}
            linkText={intl.formatMessage({
              id: 'projects.all',
              defaultMessage: 'Sjá öll verkefni',
            })}
          >
            {relatedProjects
              .map((relatedProjectContainer: Prismic_ProjectRelated_Projects) => {
                const related = relatedProjectContainer.project as Prismic_Project | null;

                if (!related) {
                  return null;
                }

                const image = related.image as PrismicImage;

                return (
                  <Card
                    heading={asText(related.title)}
                    to={linkResolver(project)}
                    image={image && <ImageContainer image={image} imageOnly />}
                  />
                );
              })
              .filter((Boolean as any) as ExcludesNull)}
          </Grid>
        </Segment>
      )}
    </>
  );
}

export const query = graphql`
  query Project($uid: String!, $lang: String = "is") {
    prismic {
      project(uid: $uid, lang: $lang) {
        _meta {
          ...meta
        }
        title
        image
        seo_title
        seo_description
        seo_image
        related_projects {
          project {
            ...project
          }
        }
        body {
          ...projectSlices
        }
      }
    }
  }
`;

Project.query = query;
Project.fragments = [
  projectSlices,
  metaFragment,
  linkableFragment,
  pageFragment,
  articleFragment,
  personFragment,
  projectFragment,
  associationFragment,
];
